// Which role owns which page, and where each role lands by default.
//
// The route guard and the post-login redirect both read from here, so the two cannot drift apart.
// That matters: "send them back to the page they were on after signing in" (TTT-80) is exactly
// how an instructor ends up staring at a student's dashboard.

export const PAGE_OWNER = {
  'dashboard-student': 'student',
  'dashboard-instructor': 'instructor',
  admin: 'admin',
  // The result page a student lands on back from JTPay's pay page (JTP-20). Only the student who
  // owns the payment can read it; a signed-out visitor signs in and comes back to it.
  'pay-result': 'student',
};

// Owned pages that are not dashboards: they keep the site's top bar and footer.
const PAGES_WITH_SITE_CHROME = new Set(['pay-result']);

// Whether a page is a dashboard, which draws its own menu in place of the site's top bar.
export function isDashboardPage(page) {
  return Boolean(PAGE_OWNER[page]) && !PAGES_WITH_SITE_CHROME.has(page);
}

const ROLE_HOME = {
  student: 'dashboard-student',
  instructor: 'dashboard-instructor',
  admin: 'admin',
};

// Where a role goes when it has nowhere particular to be.
export function homeFor(role) {
  return ROLE_HOME[role] || 'home';
}

// The page in a hash path, without its query: `dashboard-student?review=b1` → `dashboard-student`.
export function pageName(path) {
  const text = String(path ?? '');
  const q = text.indexOf('?');
  return q === -1 ? text : text.slice(0, q);
}

// The hash path after `#/`, query included. The route guard remembers this for a signed-out
// visitor, so a link like the review email's (`#/dashboard-student?review=<id>`) survives sign-in.
// It stays a hash path: the app only ever puts it back behind `#/`, so it cannot leave the site.
export function hashPath(hash) {
  return String(hash ?? '').replace(/^#\/?/, '');
}

// The booking id a review link names (`?review=<id>`), or null when the address has none.
export function reviewIdFromHash(hash) {
  const text = String(hash ?? '');
  const q = text.indexOf('?');
  if (q === -1) return null;
  return new URLSearchParams(text.slice(q + 1)).get('review') || null;
}

// The public address of an instructor's profile, the one they copy to bring their own students
// (IW-2). It is the short address (ADR 0010, FS-1.3): the API answers it with a page that carries
// the link preview, then opens the app profile. Slug first, id when they have none, the same key
// the marketplace cards link with. `siteBase` defaults to the site they signed in to; the short
// address hangs off the site root, not the app path.
export function instructorProfileUrl({ slug, id }, siteBase = window.location.origin) {
  return `${siteBase}/i/${encodeURIComponent(slug || id)}`;
}

// Pages nobody owns (the marketplace, an instructor's profile, the blog) are open to everyone,
// signed in or not. A query on the page never changes who may see it.
export function canView(role, page) {
  const owner = PAGE_OWNER[pageName(page)];
  return !owner || owner === role;
}

// Where a user goes the moment they sign in, and whether a booking they had started should come
// back with them.
//
// `intent` is what sent them to the login page: `{ page }` when a sign-in expired mid-task
// (TTT-80), plus `{ booking }` when a logged-out visitor pressed Book (TTT-82). Null when they
// simply came to log in.
export function destinationAfterLogin(intent, role) {
  // A booking belongs to the student who started it. An instructor or admin signing in on that
  // same login page goes to their own dashboard instead — they cannot take a seat.
  if (intent?.booking) {
    return role === 'student'
      ? { page: intent.page, booking: intent.booking }
      : { page: homeFor(role), booking: null };
  }

  if (intent?.page && canView(role, intent.page)) {
    return { page: intent.page, booking: null };
  }

  return { page: homeFor(role), booking: null };
}
